import type { ReactNode } from 'react';
import { cx } from '../cx';

/**
 * THE DAY RAIL (dashboard.md D27): a time GUTTER plus rows, and deliberately NOT a
 * proportional hour grid.
 *
 * TWO APPLE SOURCES GROUND THE FORM RATHER THAN ONE. The HIG's Widgets page describes
 * Apple's own Calendar at glanceable sizes - "all Calendar widgets display a person's
 * upcoming events. In each size, the widget remains centered on events while expanding
 * the range of information as the size increases" - and Calendar itself documents the
 * list as a first-class alternative: "View a complete list of upcoming events: In Day
 * view, tap the Day view icon and choose List." The hour window is itself a preference on
 * the Mac ("shows from 6 to 24 hours of events per day"), so a proportional grid is one
 * presentation of a day among several that Apple ships, not the definition of one.
 *
 * Three practical reasons finish the argument for our case: `call.duration_minutes` is
 * optional, so most rows have no height to be proportional TO; a day with three calls in
 * it is 80 percent empty grid; and a 30-minute call at 375px would be 18 vertical pixels.
 *
 * THE GUTTER IS THE ONE PLACE ON THIS SCREEN WHERE TABULAR FIGURES ARE CORRECT: a column
 * of times that must align vertically. The stat tile's number deliberately does not use
 * them.
 *
 * AN UNTIMED ROW SORTS INTO A BAND ABOVE THE TIMED ONES, which is where Calendar puts
 * all-day events ("Events that last one or more complete days are shown in the All-Day
 * section at the top of Day or Week view"). A row with no time placed IN the time column
 * would have to invent a position, and an invented position is a wrong one.
 *
 * THE NOW MARKER USES THE ACCENT, AND THAT IS A DELIBERATE DEVIATION FROM APPLE CALENDAR,
 * which draws its current-time line in red. In this product red MEANS at-risk, on this
 * screen, three panels apart; a red line across the day would read as an alarm about the
 * day. "Now" is not a status, it is a position, and the accent is the token for where you
 * are. It renders only when the shown day IS today, and it does not pulse - nothing on
 * this screen blinks.
 */
export interface DayRailRowProps {
  /** `09:30`, already formatted, or `undefined` for the untimed band. */
  readonly time?: string | undefined;
  readonly children: ReactNode;
  /**
   * A call that should have happened and has no `occurred_at`: dimmed rather than
   * removed, because a scheduled call nobody logged is information.
   */
  readonly dimmed?: boolean | undefined;
  readonly className?: string | undefined;
}

export function DayRailRow({ time, children, dimmed = false, className }: DayRailRowProps) {
  return (
    <li className={cx('flex min-h-(--row-height-dense) items-start gap-3', className)}>
      <span
        className={cx(
          'w-(--rail-gutter) shrink-0 pt-2 text-right text-2xs tabular-nums',
          dimmed ? 'text-ink-subtle' : 'text-ink-muted',
        )}
      >
        {time ?? ''}
      </span>
      <div className={cx('min-w-0 flex-1', dimmed && 'text-ink-muted')}>{children}</div>
    </li>
  );
}

/**
 * The line between the row before now and the row after it.
 *
 * BETWEEN ROWS, not at a proportional offset: the rail has no proportional axis to offset
 * within, and a marker that claimed one would be claiming a precision the layout does not
 * have.
 */
export function NowMarker({ label = 'Now' }: { readonly label?: string }) {
  return (
    <li className="flex items-center gap-3" aria-label={label}>
      <span className="w-(--rail-gutter) shrink-0 text-right text-2xs font-medium text-accent">
        {label}
      </span>
      <span className="flex flex-1 items-center gap-1" aria-hidden="true">
        <span className="size-(--now-marker-size) shrink-0 rounded-pill bg-accent" />
        <span className="h-px flex-1 bg-accent" />
      </span>
    </li>
  );
}

export interface DayRailProps {
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

export function DayRail({ children, className }: DayRailProps) {
  return <ul className={cx('space-y-1', className)}>{children}</ul>;
}
